import React, { useContext, useMemo } from 'react';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import { PropTypes as PropTypes$1 } from '@micromag/core';

/* eslint-disable react/jsx-props-no-spreading */
var StackContext = /*#__PURE__*/React.createContext({
  direction: 'horizontal'
});
var useStack = function useStack() {
  return useContext(StackContext);
};
var useStackDirection = function useStackDirection() {
  var _useContext = useContext(StackContext),
    direction = _useContext.direction;
  return direction;
};
var propTypes$4 = {
  children: PropTypes.node.isRequired,
  direction: PropTypes.oneOf(['vertical', 'horizontal'])
};
var defaultProps$4 = {
  direction: 'vertical'
};

// Note: this is done to avoid excessive renders on the screens that use the context

function StackProvider(_ref) {
  var direction = _ref.direction,
    children = _ref.children;
  var value = useMemo(function () {
    return {
      direction: direction
    };
  }, [direction]);
  return /*#__PURE__*/React.createElement(StackContext.Provider, {
    value: value
  }, children);
}
StackProvider.propTypes = propTypes$4;
StackProvider.defaultProps = defaultProps$4;

var styles$1 = {"container":"micromag-element-stack-spacer-container","hasSize":"micromag-element-stack-spacer-hasSize"};

var propTypes$3 = {
  size: PropTypes.number,
  minSize: PropTypes.number,
  maxSize: PropTypes.number,
  className: PropTypes.string
};
var defaultProps$3 = {
  size: null,
  minSize: null,
  maxSize: null,
  className: null
};
function Spacer(_ref) {
  var size = _ref.size,
    minSize = _ref.minSize,
    maxSize = _ref.maxSize,
    className = _ref.className;
  var direction = useStackDirection();
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles$1.container, _defineProperty(_defineProperty({}, styles$1.hasSize, size !== null), className, className !== null)]),
    style: {
      width: direction === 'horizontal' ? size : null,
      minWidth: direction === 'horizontal' ? minSize : null,
      maxWidth: direction === 'horizontal' ? maxSize : null,
      height: direction === 'vertical' ? size : null,
      minHeight: direction === 'vertical' ? minSize : null,
      maxHeight: direction === 'vertical' ? maxSize : null
    }
  });
}
Spacer.propTypes = propTypes$3;
Spacer.defaultProps = defaultProps$3;
Spacer.withoutTransitionsWrapper = true;

var styles = {"container":"micromag-element-stack-container"};

var propTypes$2 = {
  direction: PropTypes$1.stackDirection,
  align: PropTypes$1.stackAlign,
  spacing: PropTypes$1.stackSpacing,
  reverse: PropTypes.bool,
  size: PropTypes.number,
  minSize: PropTypes.number,
  maxSize: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
};
var defaultProps$2 = {
  direction: 'horizontal',
  align: 'center',
  spacing: null,
  reverse: false,
  size: null,
  minSize: null,
  maxSize: null,
  className: null,
  children: null
};
function Stack(_ref) {
  var direction = _ref.direction,
    align = _ref.align,
    spacing = _ref.spacing,
    reverse = _ref.reverse,
    size = _ref.size,
    minSize = _ref.minSize,
    maxSize = _ref.maxSize,
    className = _ref.className,
    children = _ref.children;
  var flexDirection = (direction === 'vertical' ? 'column' : 'row') + (reverse ? '-reverse' : '');
  var alignItems = align === 'center' ? align : "flex-".concat(align);
  var justifyContent = isString(spacing) ? "space-".concat(spacing) : null;
  var space = isNumber(spacing) ? spacing : null;
  var lastIndex = children !== null && children.length ? children.length - 1 : null;
  return /*#__PURE__*/React.createElement(StackProvider, {
    direction: direction
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    style: {
      flexDirection: flexDirection,
      alignItems: alignItems,
      justifyContent: justifyContent,
      width: direction === 'horizontal' ? size : null,
      minWidth: direction === 'horizontal' ? minSize : null,
      maxWidth: direction === 'horizontal' ? maxSize : null,
      height: direction === 'vertical' ? size : null,
      minHeight: direction === 'vertical' ? minSize : null,
      maxHeight: direction === 'vertical' ? maxSize : null
    }
  }, React.Children.toArray(children).reduce(function (allChildren, child, index) {
    return child.type !== Spacer && space !== null && index < lastIndex ? [].concat(_toConsumableArray(allChildren), [child, /*#__PURE__*/React.createElement(Spacer, {
      key: "spacer-".concat(index),
      size: space
    })]) : [].concat(_toConsumableArray(allChildren), [child]);
  }, [])));
}
Stack.propTypes = propTypes$2;
Stack.defaultProps = defaultProps$2;

/* eslint-disable react/jsx-props-no-spreading */
var propTypes$1 = {};
var defaultProps$1 = {};
function HStack(props) {
  return /*#__PURE__*/React.createElement(Stack, Object.assign({}, props, {
    direction: "horizontal"
  }));
}
HStack.propTypes = propTypes$1;
HStack.defaultProps = defaultProps$1;

/* eslint-disable react/jsx-props-no-spreading */
var propTypes = {};
var defaultProps = {};
function VStack(props) {
  return /*#__PURE__*/React.createElement(Stack, Object.assign({}, props, {
    direction: "vertical"
  }));
}
VStack.propTypes = propTypes;
VStack.defaultProps = defaultProps;

export { HStack, Spacer, StackProvider, VStack, Stack as default, useStack, useStackDirection };
